import { useState } from "react";
import { ArrowLeft, Ban, ExternalLink, RefreshCw } from "lucide-react";
import { portalApi, type PortalApiClient } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { Dialog, ErrorState, Field, LoadingState, PageHeader, StatusBadge } from "../components/Shared";
import { useAsyncData } from "../hooks/useAsyncData";
import { useRouter } from "../routing/Router";

export function JobDetailPage({ client = portalApi }: { client?: PortalApiClient }) {
  const { path, navigate } = useRouter();
  const jobId = path.split("?")[0].split("/")[2] ?? "";
  const { session } = useAuth();
  const job = useAsyncData(() => client.jobs.get(jobId), [client, jobId]);
  const [pendingAction, setPendingAction] = useState<"retry" | "cancel" | null>(null);
  const [reason, setReason] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const canManage = session?.user.role === "owner" || session?.user.role === "manager";
  const isOwner = session?.user.role === "owner";

  async function runAction() {
    if (!pendingAction || reason.trim().length < 3) return;
    setSaving(true);
    setActionError(null);
    try {
      if (pendingAction === "retry") await client.jobs.retry(jobId, reason.trim());
      else await client.jobs.cancel(jobId, reason.trim());
      setPendingAction(null);
      setReason("");
      await job.reload();
    } catch (requestError) {
      setActionError(requestError instanceof Error ? requestError.message : "The action failed.");
    } finally {
      setSaving(false);
    }
  }

  if (job.loading) return <LoadingState label="Loading job" />;
  if (job.error || !job.data) {
    return <ErrorState message={job.error ?? "Job not found."} onRetry={job.reload} />;
  }

  return (
    <>
      <button className="back-link" type="button" onClick={() => navigate(-1)}>
        <ArrowLeft size={16} />
        Back to work
      </button>
      <PageHeader
        title={job.data.title}
        description={job.data.channelName}
        actions={
          canManage && !["published", "ready_to_upload"].includes(job.data.status) ? (
            <>
              {job.data.status === "needs_attention" ? (
                <button className="button secondary" type="button" onClick={() => setPendingAction("retry")}>
                  <RefreshCw size={16} />
                  Retry
                </button>
              ) : null}
              <button className="button danger" type="button" onClick={() => setPendingAction("cancel")}>
                <Ban size={16} />
                Cancel
              </button>
            </>
          ) : undefined
        }
      />
      {actionError ? <div className="inline-alert" role="alert">{actionError}</div> : null}
      <section className="detail-grid">
        <div className="detail-main">
          <div className="detail-row"><span>Status</span><StatusBadge status={job.data.status} /></div>
          <div className="detail-row"><span>Target duration</span><strong>{job.data.durationMinutes} minutes</strong></div>
          <div className="detail-row"><span>Created</span><strong>{new Date(job.data.createdAt).toLocaleString()}</strong></div>
          {job.data.context ? <div className="detail-copy"><span>Context</span><p>{job.data.context}</p></div> : null}
          {job.data.failureDetail ? <div className="attention-box"><strong>{job.data.failureCode ?? "Needs attention"}</strong><p>{job.data.failureDetail}</p></div> : null}
          {job.data.driveUrl ? <a className="button secondary" href={job.data.driveUrl} target="_blank" rel="noreferrer"><ExternalLink size={16} />Open Drive video</a> : null}
          {job.data.youtubeUrl ? <a className="button secondary" href={job.data.youtubeUrl} target="_blank" rel="noreferrer"><ExternalLink size={16} />Open YouTube video</a> : null}
        </div>
        {isOwner ? (
          <aside className="snapshot-panel">
            <h2>Production details</h2>
            <dl>
              <div><dt>Profile</dt><dd>{job.data.profileId ?? "Not available"}</dd></div>
              <div><dt>Voice</dt><dd>{job.data.voiceId ?? "Not available"}</dd></div>
              <div><dt>Attempt</dt><dd>{job.data.attemptNumber ?? "Not started"}</dd></div>
              <div><dt>Worker</dt><dd>{job.data.workerId ?? "Unassigned"}</dd></div>
              <div><dt>Last event</dt><dd>{job.data.lastEventAt ? new Date(job.data.lastEventAt).toLocaleString() : "None"}</dd></div>
            </dl>
            <p className="table-subtext">
              The server does not expose the immutable input snapshot through this screen.
            </p>
          </aside>
        ) : null}
      </section>
      {pendingAction ? (
        <Dialog
          title={pendingAction === "retry" ? "Retry job" : "Cancel job"}
          onClose={() => setPendingAction(null)}
          actions={
            <>
              <button className="button secondary" type="button" onClick={() => setPendingAction(null)}>Back</button>
              <button className={pendingAction === "cancel" ? "button danger" : "button primary"} type="button" disabled={saving || reason.trim().length < 3} onClick={() => void runAction()}>
                {pendingAction === "retry" ? "Retry" : "Cancel job"}
              </button>
            </>
          }
        >
          <Field label="Reason" hint="At least 3 characters. This is recorded in the audit history.">
            <textarea rows={4} value={reason} onChange={(event) => setReason(event.target.value)} />
          </Field>
        </Dialog>
      ) : null}
    </>
  );
}
